import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext, API } from '@/App';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Search, RefreshCw, Eye, UserCheck, Ban, CheckCircle, Star,
  Settings, X, Building, User, AlertTriangle
} from 'lucide-react';

export default function AdminProviders() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({ search: '', blocked: '' });
  const [stats, setStats] = useState({ total: 0, active: 0, blocked: 0 });
  
  // Modal de limite
  const [limitModal, setLimitModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [newLimit, setNewLimit] = useState('');
  const [limitReason, setLimitReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || user.user_type !== 'admin') {
      navigate('/control-portal-clube/login');
      return;
    }
    fetchProviders();
  }, [user, navigate]);

  const fetchProviders = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allProviders = res.data.providers || [];
      setProviders(allProviders);
      
      setStats({
        total: allProviders.length,
        active: allProviders.filter(p => !p.blocked).length,
        blocked: allProviders.filter(p => p.blocked).length
      });
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar prestadores');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleBlock = async (providerId, blocked, e) => {
    e.stopPropagation();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/block-user`, {
        user_id: providerId,
        blocked: blocked
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(blocked ? 'Prestador bloqueado' : 'Prestador desbloqueado');
      fetchProviders(true);
    } catch (error) {
      toast.error('Erro ao alterar status');
    }
  };

  const openLimitModal = (provider, e) => {
    e.stopPropagation();
    setSelectedProvider(provider);
    setNewLimit(provider.settings?.daily_limit || provider.daily_limit || 30);
    setLimitReason('');
    setLimitModal(true);
  };

  const handleUpdateLimit = async () => {
    if (!selectedProvider) return;
    
    const limit = parseInt(newLimit);
    if (isNaN(limit) || limit < 5 || limit > 50) {
      toast.error('Limite deve estar entre 5 e 50');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/admin/provider/limit`, {
        provider_id: selectedProvider.id,
        daily_limit: limit,
        reason: limitReason || 'Ajuste administrativo'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Limite atualizado!');
      setLimitModal(false);
      fetchProviders(true);
    } catch (error) {
      toast.error('Erro ao atualizar limite');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  // Filtrar
  const filteredProviders = providers.filter(provider => {
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const matches = 
        provider.name?.toLowerCase().includes(search) ||
        provider.business_name?.toLowerCase().includes(search) ||
        provider.email?.toLowerCase().includes(search) ||
        provider.cpf_cnpj?.includes(search);
      if (!matches) return false;
    }
    if (filters.blocked === 'blocked' && !provider.blocked) return false;
    if (filters.blocked === 'active' && provider.blocked) return false;
    return true;
  });

  const clearFilters = () => setFilters({ search: '', blocked: '' });
  const hasFilters = filters.search || filters.blocked;

  if (loading) {
    return (
      <AdminLayout title="Prestadores" subtitle="Carregando...">
        <div className="space-y-6">
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-12" />
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24" />)}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Prestadores</h1>
            <p className="text-slate-500">Gerencie todos os prestadores de serviço</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchProviders(true)} disabled={refreshing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card 
            className={`p-4 cursor-pointer transition-all ${!filters.blocked ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, blocked: '' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <UserCheck className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Total</p>
              </div>
            </div>
          </Card>
          
          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.blocked === 'active' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, blocked: 'active' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.active}</p>
                <p className="text-xs text-slate-500">Ativos</p>
              </div>
            </div>
          </Card>

          <Card 
            className={`p-4 cursor-pointer transition-all ${filters.blocked === 'blocked' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters({ ...filters, blocked: 'blocked' })}
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Ban className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.blocked}</p>
                <p className="text-xs text-slate-500">Bloqueados</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nome, e-mail ou documento..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </div>
            {hasFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" /> Limpar
              </Button>
            )}
          </div>
        </Card>

        {/* Providers List */}
        <div className="space-y-3">
          {filteredProviders.length > 0 ? (
            filteredProviders.map((provider) => (
              <Card 
                key={provider.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/control-portal-clube/providers/${provider.id}`)}
              >
                <div className="flex items-center gap-4">
                  <Avatar className="w-14 h-14">
                    <AvatarFallback className="bg-green-100 text-green-700">
                      {getInitials(provider.business_name || provider.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-semibold truncate">{provider.business_name || provider.name}</p>
                      {provider.blocked ? (
                        <Badge className="bg-red-100 text-red-700">Bloqueado</Badge>
                      ) : (
                        <Badge className="bg-green-100 text-green-700">Ativo</Badge>
                      )}
                      {provider.doc_type === 'cnpj' ? (
                        <Badge variant="outline" className="gap-1">
                          <Building className="w-3 h-3" /> PJ
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <User className="w-3 h-3" /> PF
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500">{provider.email}</p>
                    <div className="flex items-center gap-4 text-xs text-slate-400 mt-1">
                      <span>{provider.doc_type === 'cnpj' ? 'CNPJ' : 'CPF'}: {provider.cpf_cnpj || 'N/A'}</span>
                      <span>•</span>
                      <span>Limite: {provider.settings?.daily_limit || provider.daily_limit || 30}/dia</span>
                      <span>•</span>
                      <span>Desde: {formatDate(provider.created_at)}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={(e) => openLimitModal(provider, e)}
                      title="Ajustar limite"
                    >
                      <Settings className="w-4 h-4" />
                    </Button>
                    {provider.blocked ? (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-green-600"
                        onClick={(e) => handleBlock(provider.id, false, e)}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Desbloquear
                      </Button>
                    ) : (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600"
                        onClick={(e) => handleBlock(provider.id, true, e)}
                      >
                        <Ban className="w-4 h-4 mr-1" /> Bloquear
                      </Button>
                    )}
                    <Button variant="ghost" size="sm">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Card className="p-12 text-center">
              <UserCheck className="w-16 h-16 mx-auto mb-4 text-slate-200" />
              <h3 className="text-lg font-semibold mb-2">Nenhum prestador encontrado</h3>
              <p className="text-slate-500">
                {hasFilters 
                  ? 'Tente ajustar os filtros de busca'
                  : 'Os prestadores cadastrados aparecerão aqui'}
              </p>
              {hasFilters && (
                <Button variant="outline" className="mt-4" onClick={clearFilters}>
                  Limpar filtros
                </Button>
              )}
            </Card>
          )}
        </div>

        {/* Summary */}
        {filteredProviders.length > 0 && (
          <p className="text-sm text-slate-500 text-center">
            Mostrando {filteredProviders.length} de {stats.total} prestadores
          </p>
        )}
      </div>

      {/* Modal de Limite */}
      <Dialog open={limitModal} onOpenChange={setLimitModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajustar Limite Diário</DialogTitle>
          </DialogHeader>
          {selectedProvider && (
            <div className="space-y-4 py-4">
              <div>
                <p className="text-sm text-slate-600">
                  Prestador: <strong>{selectedProvider.business_name || selectedProvider.name}</strong>
                </p>
                <p className="text-sm text-slate-500">
                  Limite atual: {selectedProvider.settings?.daily_limit || selectedProvider.daily_limit || 30} atendimentos/dia
                </p>
              </div>
              <div>
                <Label>Novo Limite (5-50)</Label>
                <Input
                  type="number"
                  min={5}
                  max={50}
                  value={newLimit}
                  onChange={(e) => setNewLimit(e.target.value)}
                />
              </div>
              <div>
                <Label>Motivo (opcional)</Label>
                <Textarea
                  value={limitReason}
                  onChange={(e) => setLimitReason(e.target.value)}
                  placeholder="Descreva o motivo da alteração..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setLimitModal(false)}>Cancelar</Button>
            <Button onClick={handleUpdateLimit} disabled={saving}>
              {saving ? 'Salvando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
